import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardHeader, CardTitle, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Loader2, ChevronLeft, ChevronRight, CheckCircle2, Clock, Mail, X, Send } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const MONTHS = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];

const fmt = (v) =>
  v != null && !isNaN(v)
    ? new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v)
    : "-";

const statusMeta = {
  paid: { label: "Payé", cls: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
  pending: { label: "En attente", cls: "bg-amber-100 text-amber-800", icon: Clock },
  not_generated: { label: "Non généré", cls: "bg-slate-100 text-slate-600", icon: X },
  skipped: { label: "Skip", cls: "bg-slate-100 text-slate-500", icon: X },
};

export default function AdvancePaymentsMonthlyDashboard({ token }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/hr/advance-payments/monthly-status?year=${year}&month=${month}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [year, month, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const shift = (delta) => {
    let m = month + delta;
    let y = year;
    if (m < 1) { m = 12; y--; }
    if (m > 12) { m = 1; y++; }
    setMonth(m); setYear(y);
  };

  const sendRemindersNow = async () => {
    if (!window.confirm("Envoyer manuellement les rappels J-1/J0 en attente maintenant ?")) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/hr/advance-payments/send-reminders`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Rappels envoyés : J-1=${res.data.j1_sent || 0}, J0=${res.data.j0_sent || 0}`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSending(false);
    }
  };

  const employees = data?.employees || [];
  const totals = data?.totals || {};

  return (
    <div className="space-y-4" data-testid="advance-monthly-dashboard">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-indigo-600" />
              État des acomptes — {MONTHS[month - 1]} {year}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={() => shift(-1)}><ChevronLeft className="w-4 h-4" /></Button>
              <Button variant="outline" size="icon" onClick={() => shift(1)}><ChevronRight className="w-4 h-4" /></Button>
              <Button onClick={sendRemindersNow} disabled={sending} className="ml-2">
                {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Envoyer les rappels maintenant
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : employees.length === 0 ? (
            <div className="py-8 text-center text-slate-500 text-sm">Aucun salarié avec acompte configuré.</div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                <StatCard label="Salariés" value={totals.employees_count} />
                <StatCard label="Total mensuel" value={fmt(totals.total_amount)} />
                <StatCard label="Payé" value={fmt(totals.total_paid)} color="emerald" />
                <StatCard label="En attente" value={fmt(totals.total_pending)} color="amber" />
                <StatCard label="Rappels envoyés" value={`J-2: ${totals.j2_sent || 0} · J-1: ${totals.j1_sent} · J0: ${totals.j0_sent}`} />
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="text-xs bg-slate-50">
                    <TableHead>Salarié</TableHead>
                    <TableHead className="text-right">Montant</TableHead>
                    <TableHead>Échéance</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-center">Rappel J-2</TableHead>
                    <TableHead className="text-center">Rappel J-1</TableHead>
                    <TableHead className="text-center">Rappel J0</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {employees.map((e) => {
                    const st = statusMeta[e.status] || statusMeta.not_generated;
                    const Ico = st.icon;
                    return (
                      <TableRow key={e.employee_id} data-testid={`advance-row-${e.employee_id}`}>
                        <TableCell className="font-medium">{e.employee_name}</TableCell>
                        <TableCell className="text-right">{fmt(e.amount)}</TableCell>
                        <TableCell>{e.due_date || `${e.due_day || "?"} du mois`}</TableCell>
                        <TableCell>
                          <Badge className={st.cls}>
                            <Ico className="w-3 h-3 mr-1" />
                            {st.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          {e.reminder_j2_sent ? (
                            <span className="inline-flex items-center gap-1 text-emerald-700 text-xs" title={e.reminder_j2_sent_at || ""}>
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              {e.reminder_j2_sent_at ? new Date(e.reminder_j2_sent_at).toLocaleDateString("fr-FR") : "envoyé"}
                            </span>
                          ) : (
                            <span className="text-slate-400 text-xs">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {e.reminder_j1_sent ? (
                            <span className="inline-flex items-center gap-1 text-emerald-700 text-xs" title={e.reminder_j1_sent_at || ""}>
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              {e.reminder_j1_sent_at ? new Date(e.reminder_j1_sent_at).toLocaleDateString("fr-FR") : "envoyé"}
                            </span>
                          ) : (
                            <span className="text-slate-400 text-xs">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {e.reminder_j0_sent ? (
                            <span className="inline-flex items-center gap-1 text-emerald-700 text-xs" title={e.reminder_j0_sent_at || ""}>
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              {e.reminder_j0_sent_at ? new Date(e.reminder_j0_sent_at).toLocaleDateString("fr-FR") : "envoyé"}
                            </span>
                          ) : (
                            <span className="text-slate-400 text-xs">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ label, value, color = "slate" }) {
  return (
    <div className={`rounded-lg border p-3 bg-white`}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`text-lg font-semibold text-${color}-700 mt-0.5`}>{value}</div>
    </div>
  );
}
